import { compactMap } from '@lessonup/utils';
import {
  fromPairs,
  intersection,
  isArray,
  isBoolean,
  isEmpty as isEmptyLodash,
  isEqual,
  isNil,
  isString,
  mapValues,
  omit,
  omitBy,
  pick,
} from 'lodash';
import qs from 'qs';
import { DefaultUrlFacets, Facet, FacetKey, Facets, SelectedFacet } from './Facets';
import { IndexType } from './IndexType';

export interface SearchParams {
  index: IndexType;
  text: string | undefined;
  facets: SelectedFacet[];
  fixedFacets?: {
    organization?: string[];
  };
  size?: number;
  from?: number;
  isMulti?: boolean;
}

export type SearchUrlParams = {
  [key in DefaultUrlFacets]?: string;
};

export interface SearchRouteParams extends SearchUrlParams {
  language?: string;
  qs?: string;
}

function parseNumberParam(param?: string): number | undefined {
  if (!param || param.length < 1) return undefined;
  return parseInt(param, 10);
}

export namespace SearchParams {
  export function facetsFromParams(
    obj: _.Dictionary<string | string[] | boolean>,
    possibleFacets: Facet[]
  ): SelectedFacet[] {
    return compactMap(possibleFacets, (f): SelectedFacet | undefined => {
      const rawValue = obj[f.key];

      if (isNil(rawValue)) {
        return undefined;
      }

      let value: string[];

      if (isArray(rawValue)) {
        value = rawValue;
      } else if (isBoolean(rawValue)) {
        value = [rawValue.toString()];
      } else {
        value = rawValue.split(',');
      }

      if (value.length === 0 || (value.length === 1 && value[0] === SearchParams.placeHolder)) {
        return undefined;
      }

      const facet: SelectedFacet = {
        key: f.key,
        value,
      };

      if (f.fixed !== undefined) {
        facet.fixed = f.fixed;
      }

      return facet;
    });
  }

  export function setFacet(
    searchParams: SearchParams,
    key: FacetKey,
    value: string,
    possibleFacets: Facet[]
  ): SearchParams {
    const toAdd = facetsFromParams(
      {
        [key]: value,
      },
      possibleFacets
    );
    const newFacets = searchParams.facets.filter((f) => f.key !== key).concat(toAdd);
    return {
      ...searchParams,
      facets: newFacets,
    };
  }

  export function toDictionary(params: SearchParams): _.Dictionary<string> {
    const pairs = params.facets.map((f) => [f.key, f.value.join(',')]);
    const dict = fromPairs(pairs);
    dict['text'] = params.text;
    dict['size'] = params.size;
    dict['from'] = params.from;
    dict['isMulti'] = params.isMulti;

    return omitBy(dict, (v) => isNil(v) || (isString(v) && isEmptyLodash(v)));
  }

  export const urlOrder: DefaultUrlFacets[] = ['country', 'subjects', 'schoolType', 'levels', 'years'];
  const urlOrderReversed = urlOrder.slice().reverse();

  type DictOrParams = Record<string, string> | SearchUrlParams;
  export const placeHolder = 'any' as const;
  export function searchUrlPostFix(obj: DictOrParams = {}): string {
    const lastIndex = urlOrderReversed.findIndex((k) => !!obj[k]);
    if (lastIndex == -1) return '';
    const deep = urlOrderReversed.length - lastIndex;
    const keysToInclude = urlOrder.slice(0, deep);
    return keysToInclude.map((k) => obj[k] || placeHolder).join('/');
  }

  export function defaultDictFromParams(params: SearchParams) {
    const pairs = params.facets.map((f) => [f.key, f.value]);
    return defaultDictFromDictionary(fromPairs(pairs));
  }

  export function defaultDictFromDictionary(obj: Record<string, string>) {
    return pick(obj, urlOrder) as SearchUrlParams;
  }

  export function searchParamsToUrlComponents(params: SearchParams | undefined) {
    if (!params) {
      return {
        queryString: '',
        urlDict: {},
      };
    }
    const obj = toDictionary(params);
    const dict = pick(obj, urlOrder) as SearchUrlParams;
    const urlDict = mapValues(dict, (v) => v && encodeURIComponent(v));
    const queryDict = omitBy(obj, (value, key) => urlDict[key] !== undefined);

    const queryString = qs.stringify(queryDict);
    return {
      queryString,
      urlDict,
    };
  }

  interface FromUrlParams {
    urlParams: SearchUrlParams | undefined;
    queryString: string;
    indexType: IndexType;
    subType?: Facets.FacetSubTypes[];
  }
  export function fromUrlComponents(params: FromUrlParams) {
    const { urlParams, queryString, indexType, subType } = params;
    const qsDict = qs.parse(queryString, {
      ignoreQueryPrefix: true,
    }) as Record<string, string>;
    const decoded = mapValues(urlParams, (v) => v && decodeURIComponent(v));
    return fromDictionary(Object.assign({}, decoded, qsDict), indexType, subType);
  }

  export function removeChannelFromFacets(searchParams: SearchParams): SearchParams {
    return {
      ...searchParams,
      facets: searchParams.facets.filter((facet) => facet.key !== 'channel'),
    };
  }

  export function fromDictionary(
    obj: Record<string, string | boolean>,
    indexType: IndexType,
    subType?: Facets.FacetSubTypes[]
  ): SearchParams {
    const possibleFacets = Facets.facetsForType(indexType, subType);

    const facets = facetsFromParams(obj, possibleFacets);

    // Helper function to safely get string value or convert boolean to string
    const getStringValue = (key: string): string | undefined => {
      const value = obj[key];
      if (isString(value)) {
        return value;
      }
      if (isBoolean(value)) {
        return value.toString();
      }
      return undefined;
    };

    // Helper function to safely get boolean value
    const getBooleanValue = (key: string): boolean | undefined => {
      const value = obj[key];
      if (isBoolean(value)) {
        return value;
      }
      if (isString(value)) {
        return value === 'true';
      }
      return undefined;
    };

    return {
      index: indexType,
      text: getStringValue('text') || '',
      facets,
      size: parseNumberParam(getStringValue('size')),
      from: parseNumberParam(getStringValue('from')),
      isMulti: getBooleanValue('isMulti') || false,
    };
  }

  export function paramsFromQueryString(queryString: string, indexType: IndexType): SearchParams {
    const obj = qs.parse(queryString, {
      ignoreQueryPrefix: true,
    }) as Record<string, string>;
    return fromDictionary(obj, indexType);
  }

  export function queryStringFromSearchParams(params: SearchParams | undefined) {
    return params ? qs.stringify(toDictionary(params)) : '';
  }

  export function isEmpty(params: SearchParams): boolean {
    return params.facets.length === 0 && isEmptyLodash(params.text);
  }

  export function hasFacet(params: SearchParams | undefined, string: string): boolean {
    if (!params) return false;
    return params.facets.some((facet) => facet.key === string);
  }

  export function facetIs(params: SearchParams | undefined, key: string, value: string | string[]): boolean {
    if (!params) return false;
    const facet = params.facets.find((facet) => facet.key === key);
    const facetValue = facet?.value;
    const facetIsArray = Array.isArray(facetValue);
    const valueIsArray = Array.isArray(value);
    if (valueIsArray) {
      return facetIsArray
        ? !!intersection(value, facet!.value).length
        : !!(facetValue && value.includes(facetValue as string));
    }
    return facet?.value.includes(value) ?? false;
  }

  export function replaceHistoryState(oldParams: SearchParams, newParams: SearchParams): boolean {
    if (!isEqual(omit(oldParams, ['size', 'from']), omit(newParams, ['size', 'from']))) {
      return false;
    }

    return Boolean(oldParams.size !== newParams.size);
  }
}
